import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getEpg, geVideoPlaybackURL } from '../Api/api';
import Player from '../tv/LiveTv/player/Player';
import { PLATFORM } from '../constant';
import { useLocation, useNavigate } from 'react-router-dom';

// Mock spatial navigation hooks (simplified for demo)
const FocusContext = React.createContext();

const useFocusable = ({ focusKey, onArrowPress, onEnterPress, isFocusBoundary, trackChildren, autoRestoreFocus, saveLastFocusedChild, preferredChildFocusKey }) => {
    const ref = useRef(null);
    const [focused, setFocused] = useState(false);

    const setFocus = useCallback((key) => {
        console.log(`Setting focus to: ${key}`);
        setFocused(key === focusKey);
    }, [focusKey]);

    const focusSelf = useCallback(() => {
        setFocused(true);
    }, []);

    // Simulate keyboard handling
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!focused) return;

            let direction;
            switch (e.key) {
                case 'ArrowUp': direction = 'up'; break;
                case 'ArrowDown': direction = 'down'; break;
                case 'ArrowLeft': direction = 'left'; break;
                case 'ArrowRight': direction = 'right'; break;
                case 'Enter':
                    if (onEnterPress) onEnterPress();
                    return;
                default: return;
            }

            if (onArrowPress) {
                onArrowPress(direction);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focused, onArrowPress, onEnterPress]);

    return { ref, focused, focusKey, setFocus, focusSelf };
};


// Channel Info Bar Component
const ChannelInfoBar = ({ channel, channelIndex, isVisible, textColor, bgColor }) => {
    const getCurrentProgram = (programList) => {
        const now = new Date().getTime();
        for (const program of programList || []) {
            const startTime = typeof program.programStartDateTime === 'string'
                ? new Date(program.programStartDateTime).getTime()
                : program.programStartDateTime;
            const endTime = typeof program.programEndDateTime === 'string'
                ? new Date(program.programEndDateTime).getTime()
                : program.programEndDateTime;

            if (now >= startTime && now <= endTime) {
                return program.programTitle || "No title";
            }
        }
        return "No current program";
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getProgress = (programList) => {
        const now = new Date().getTime();
        for (const program of programList || []) {
            const startTime = typeof program.programStartDateTime === 'string'
                ? new Date(program.programStartDateTime).getTime()
                : program.programStartDateTime;
            const endTime = typeof program.programEndDateTime === 'string'
                ? new Date(program.programEndDateTime).getTime()
                : program.programEndDateTime;

            if (now >= startTime && now <= endTime) {
                const progress = ((now - startTime) / (endTime - startTime)) * 100;
                return { progress, startTime, endTime };
            }
        }
        return { progress: 0, startTime: null, endTime: null };
    };

    if (!isVisible) return null;

    const { progress, startTime, endTime } = getProgress(channel.programList);

    return (
        <div
            style={{
                position: 'absolute',
                left: '2.5%',
                right: '2.5%',
                bottom: '30px',
                width: '95%',
                padding: '20px 24px',
                backgroundColor: `${bgColor}DD`,
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: `1px solid ${textColor}20`,
                display: 'flex',
                alignItems: 'center',
                zIndex: 1000,
                animation: 'slideUp 0.3s ease-out'
            }}
        >
            <style jsx>{`
                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
            `}</style>

            <img
                src={channel.ottAsset?.Posters?.[0]?.Url || ''}
                alt="Channel Logo"
                style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '12px',
                    objectFit: 'cover',
                    backgroundColor: bgColor,
                    border: `2px solid ${textColor}10`
                }}
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                }}
            />
            <div
                style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '12px',
                    backgroundColor: `${textColor}10`,
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: `${textColor}80`,
                    fontSize: '24px'
                }}
            >
                📺
            </div>

            <div style={{ marginLeft: '20px', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                        style={{
                            padding: '4px 12px',
                            backgroundColor: `${textColor}15`,
                            borderRadius: '8px',
                            color: textColor,
                            fontSize: '16px',
                            fontWeight: '700',
                            letterSpacing: '1px',
                            marginRight: '12px'
                        }}
                    >
                        {`CH ${channelIndex + 1}`}
                    </div>
                    <div
                        style={{
                            color: textColor,
                            fontSize: '20px',
                            fontWeight: '700',
                            letterSpacing: '0.3px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                        }}
                    >
                        {channel.channelDisplayName || channel.channelName || "Channel"}
                    </div>
                </div>

                <div
                    style={{
                        color: `${textColor}CC`,
                        fontSize: '16px',
                        fontWeight: '500',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: '8px'
                    }}
                >
                    {getCurrentProgram(channel.programList)}
                </div>

                {startTime && endTime && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ color: `${textColor}99`, fontSize: '14px', fontWeight: '500' }}>
                            {formatTime(startTime)}
                        </span>

                        <div style={{ flex: 1, height: '4px', backgroundColor: `${textColor}20`, borderRadius: '2px', overflow: 'hidden' }}>
                            <div
                                style={{
                                    height: '100%',
                                    width: `${progress}%`,
                                    backgroundColor: textColor,
                                    borderRadius: '2px',
                                    transition: 'width 1s ease-out'
                                }}
                            />
                        </div>

                        <span style={{ color: `${textColor}99`, fontSize: '14px', fontWeight: '500' }}>
                            {formatTime(endTime)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Left Menu Component
const LeftMenu = ({
    channels,
    selectedMenuIndex,
    isVisible,
    textColor,
    bgColor,
    pageTitle,
    onChannelSelect,
    onMenuIndexChange,
    setMenuFocusRef,
    setMainFocus,
    setIsLeftMenuOpen
}) => {
    const menuListRef = useRef(null);
    const [currentFocusedIndex, setCurrentFocusedIndex] = useState(selectedMenuIndex);

    const { ref: menuFocusRef, focusKey: menuFocusKey, setFocus: setMenuFocus } = useFocusable({
        focusKey: "left-menu",
        isFocusBoundary: true,
        trackChildren: true,
        autoRestoreFocus: true,
        onArrowPress: (direction) => {
            // Handle right arrow to close menu
            if (direction === 'right') {
                setIsLeftMenuOpen(false);
                return true;
            }
            // Handle up/down navigation within the menu
            if (direction === 'up') {
                const prevIndex = (currentFocusedIndex - 1 + channels.length) % channels.length;
                setCurrentFocusedIndex(prevIndex);
                onMenuIndexChange(prevIndex);
                return true;
            } else if (direction === 'down') {
                const nextIndex = (currentFocusedIndex + 1) % channels.length;
                setCurrentFocusedIndex(nextIndex);
                onMenuIndexChange(nextIndex);
                return true;
            }
            return false;
        },
        onEnterPress: () => {
            onChannelSelect(currentFocusedIndex);
            return true;
        }
    });

    // Expose setMenuFocus to parent component
    useEffect(() => {
        if (setMenuFocusRef) {
            setMenuFocusRef.current = setMenuFocus;
        }
    }, [setMenuFocus, setMenuFocusRef]);

    // Update currentFocusedIndex when selectedMenuIndex changes from outside
    useEffect(() => {
        setCurrentFocusedIndex(selectedMenuIndex);
    }, [selectedMenuIndex]);

    const getCurrentProgram = (programList) => {
        const now = new Date().getTime();
        for (const program of programList || []) {
            const startTime = typeof program.programStartDateTime === 'string'
                ? new Date(program.programStartDateTime).getTime()
                : program.programStartDateTime;
            const endTime = typeof program.programEndDateTime === 'string'
                ? new Date(program.programEndDateTime).getTime()
                : program.programEndDateTime;

            if (now >= startTime && now <= endTime) {
                return program.programTitle || "No title";
            }
        }
        return "No current program";
    };

    // Focus management when menu becomes visible
    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(() => {
                setMenuFocus("left-menu");
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isVisible, setMenuFocus]);

    // Scroll to focused item
    useEffect(() => {
        if (menuListRef.current && isVisible) {
            const focusedElement = menuListRef.current.querySelector(`[data-channel-index="${currentFocusedIndex}"]`);
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [currentFocusedIndex, isVisible]);

    if (!isVisible) return null;

    return (
        <FocusContext.Provider value={menuFocusKey}>
            <div
                ref={menuFocusRef}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '320px',
                    backgroundColor: `${bgColor}F0`,
                    boxShadow: '4px 0 24px rgba(0,0,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 2000,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.3s ease-out'
                }}
            >
                <div
                    style={{
                        padding: '28px 16px 16px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: `1px solid ${textColor}15`
                    }}
                >
                    <div
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: `${textColor}15`,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: textColor,
                            fontSize: '24px'
                        }}
                    >
                        📺
                    </div>
                    <div
                        style={{
                            color: textColor,
                            fontSize: '18px',
                            fontWeight: '700',
                            letterSpacing: '0.5px',
                            marginLeft: '16px'
                        }}
                    >
                        {pageTitle}
                    </div>
                </div>

                <div
                    ref={menuListRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '8px 0',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}
                >
                    <style jsx>{`
                        div::-webkit-scrollbar {
                            display: none;
                        }
                    `}</style>
                    {channels.map((channel, index) => (
                        <div
                            key={channel.id || index}
                            data-channel-index={index}
                            style={{
                                margin: '16px 12px',
                                paddingRight: '16px',
                                backgroundColor: (index === selectedMenuIndex || index === currentFocusedIndex) ? `${textColor}18` : 'transparent',
                                borderRadius: '12px',
                                border: (index === selectedMenuIndex || index === currentFocusedIndex) ? `2px solid ${textColor}40` : '2px solid transparent',
                                boxShadow: (index === selectedMenuIndex || index === currentFocusedIndex) ? `0 4px 20px ${textColor}15` : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                transform: (index === selectedMenuIndex || index === currentFocusedIndex) ? 'scale(1.02)' : 'scale(1)'
                            }}
                        >
                            <img
                                src={channel.ottAsset?.Posters?.[0]?.Url || ''}
                                alt="Channel"
                                style={{
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: '10px',
                                    objectFit: 'cover',
                                    backgroundColor: bgColor,
                                    border: `1px solid ${textColor}20`
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '10px',
                                    backgroundColor: `${textColor}10`,
                                    display: 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: `${textColor}60`,
                                    fontSize: '20px'
                                }}
                            >
                                📺
                            </div>

                            <div style={{ marginLeft: '16px', flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <div
                                        style={{
                                            padding: '3px 10px',
                                            backgroundColor: `${textColor}20`,
                                            borderRadius: '8px',
                                            color: (index === selectedMenuIndex || index === currentFocusedIndex) ? textColor : `${textColor}90`,
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            letterSpacing: '0.5px',
                                            marginRight: '10px'
                                        }}
                                    >
                                        {`CH ${index + 1}`}
                                    </div>
                                    <div
                                        style={{
                                            color: (index === selectedMenuIndex || index === currentFocusedIndex) ? textColor : `${textColor}B0`,
                                            fontSize: '16px',
                                            fontWeight: (index === selectedMenuIndex || index === currentFocusedIndex) ? '700' : '500',
                                            letterSpacing: '0.2px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                        }}
                                    >
                                        {channel.channelDisplayName || channel.channelName || "Channel"}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        color: (index === selectedMenuIndex || index === currentFocusedIndex) ? `${textColor}D0` : `${textColor}70`,
                                        fontSize: '12px',
                                        fontWeight: '400',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {getCurrentProgram(channel.programList)}
                                </div>
                            </div>

                            {(index === selectedMenuIndex || index === currentFocusedIndex) && (
                                <div style={{
                                    marginLeft: '8px',
                                    color: `${textColor}B0`,
                                    fontSize: '18px',
                                    transform: 'translateX(2px)'
                                }}>
                                    ▶
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </FocusContext.Provider>
    );
};

// Main TV Player Component
const TVPlayerMain = ({ initialIndex = 0, pageTitle = "Live TV", focusKeyParam = "tv-player" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const asset = location.state?.asset ?? null;
    const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(false);
    const [selectedMenuIndex, setSelectedMenuIndex] = useState(initialIndex);
    const [showChannelInfo, setShowChannelInfo] = useState(false);
    const [channels, setChannels] = useState([]);
    const [videoURL, setVideoURL] = useState('');
    const [loading, setLoading] = useState(true);
    const infoTimerRef = useRef(null);
    const menuFocusRef = useRef(null);

    // Theme colors
    const textColor = '#FFFFFF';
    const bgColor = '#000000';

    const { ref: mainRef, focusKey: mainFocusKey, setFocus: setMainFocus, focusSelf } = useFocusable({
        focusKey: focusKeyParam,
        saveLastFocusedChild: true,
        trackChildren: true,
        autoRestoreFocus: true,
        isFocusBoundary: false,
        onArrowPress: (direction) => {
            console.log(`${direction} arrow pressed on main player`);
            if (!isLeftMenuOpen) {
                switch (direction) {
                    case 'left':
                        setIsLeftMenuOpen(true);
                        setShowChannelInfo(false);
                        // Focus will be handled in the LeftMenu component
                        return true;
                    case 'up':
                        const prevIndex = (selectedMenuIndex - 1 + channels.length) % channels.length;
                        setSelectedMenuIndex(prevIndex);
                        playChannel(channels[prevIndex]);
                        showChannelInfoBar();
                        return true;
                    case 'down':
                        const nextIndex = (selectedMenuIndex + 1) % channels.length;
                        setSelectedMenuIndex(nextIndex);
                        playChannel(channels[nextIndex]);
                        showChannelInfoBar();
                        return true;
                }
            }
            return false;
        },
        onEnterPress: () => {
            if (!isLeftMenuOpen) {
                showChannelInfoBar();
                return true;
            }
            return true;
        }
    });

    const showChannelInfoBar = useCallback(() => {
        if (infoTimerRef.current) {
            clearTimeout(infoTimerRef.current);
        }
        setShowChannelInfo(true);
        infoTimerRef.current = setTimeout(() => {
            setShowChannelInfo(false);
        }, 6000);
    }, []);

    const playChannel = useCallback(async (channel) => {
        setLoading(true);
        console.log('Playing channel:', channel.channelName);
        let res = await geVideoPlaybackURL(channel.ottAsset, PLATFORM[0].SID);
        if (res.success) {
            setVideoURL(res.data.url);
        }
        setLoading(false);
        // Video playback logic would go here
    }, []);

    const handleChannelSelect = useCallback((index) => {
        setSelectedMenuIndex(index);
        setIsLeftMenuOpen(false);
        playChannel(channels[index]);
        showChannelInfoBar();
        setTimeout(() => {
            focusSelf();
        }, 100);
    }, [channels, playChannel, showChannelInfoBar, focusSelf]);

    const handleMenuIndexChange = useCallback((index) => {
        // This is called when navigating in menu but not selecting
        // We can use this to preview channels if needed
        console.log('Menu focus changed to:', index);
    }, []);

    useEffect(() => {
        loadEpg();
    }, []);

    const loadEpg = async () => {
        setLoading(true);
        let res = await getEpg();
        let chan = res.data.map(channel => ({
            id: channel.SID,
            channelName: channel.channelName,
            channelDisplayName: channel.channelDisplayName,
            ottAsset: channel.ottAsset,
            programList: channel.programList.map(program => ({
                programTitle: program.title,
                programStartDateTime: new Date(program.startdatetime).getTime() - 3600000,
                programEndDateTime: new Date(program.enddatetime).getTime() + 3600000
            }))

        }));
        setChannels(chan);
        if (asset) {
            const channelIndex = chan.findIndex(c => c.ottAsset._id === asset._id);
            if (channelIndex !== -1) {
                setSelectedMenuIndex(channelIndex);
                playChannel(chan[channelIndex]);
            }
        }
        console.log('EPG loaded:', res.data);
        setLoading(false);
    }

    // Initialize focus and first channel on mount
    useEffect(() => {
        if (channels.length > 0) {
            setTimeout(() => {
                setMainFocus(focusKeyParam);
                focusSelf();
            }, 100);

            showChannelInfoBar();
            playChannel(channels[selectedMenuIndex]);
        }
    }, [setMainFocus, focusKeyParam, focusSelf, showChannelInfoBar, playChannel, channels, selectedMenuIndex]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (infoTimerRef.current) {
                clearTimeout(infoTimerRef.current);
            }
        };
    }, []);

    const currentChannel = useMemo(() => channels[selectedMenuIndex], [channels, selectedMenuIndex]);

    return (
        <FocusContext.Provider value={mainFocusKey}>
            <div
                ref={mainRef}
                style={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: bgColor,
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
            >
                {/* Video Player Background */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "black",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {loading ? <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: 'white' }} /> : <Player src={videoURL} asset={currentChannel?.ottAsset} showControls={false} />}
                </div>

                {/* Left Menu Overlay */}
                <LeftMenu
                    channels={channels}
                    selectedMenuIndex={selectedMenuIndex}
                    isVisible={isLeftMenuOpen}
                    textColor={textColor}
                    bgColor={bgColor}
                    pageTitle={pageTitle}
                    onChannelSelect={handleChannelSelect}
                    onMenuIndexChange={handleMenuIndexChange}
                    setMenuFocusRef={menuFocusRef}
                    setMainFocus={setMainFocus}
                    setIsLeftMenuOpen={setIsLeftMenuOpen}
                />

                {/* Menu Background Overlay */}
                {isLeftMenuOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: '320px',
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            zIndex: 1500,
                            opacity: isLeftMenuOpen ? 1 : 0,
                            transition: 'opacity 0.3s ease-out'
                        }}
                    />
                )}

                {/* Channel Info Bar */}
                {currentChannel && (
                    <ChannelInfoBar
                        channel={currentChannel}
                        channelIndex={selectedMenuIndex}
                        isVisible={showChannelInfo && !isLeftMenuOpen}
                        textColor={textColor}
                        bgColor={bgColor}
                    />
                )}
            </div>
        </FocusContext.Provider>
    );
};

export default TVPlayerMain;